import { Timestamp } from 'firebase/firestore';

/**
 * Types of documents that can be generated and sent
 */
export enum DocumentType {
  Invoice = 'invoice',
  PaymentReminder = 'payment_reminder',
  PaymentRequest = 'payment_request',
  Indexation = 'indexation',
  ContractTermination = 'contract_termination',
  ContractRenewal = 'contract_renewal',
  MaintenanceNotice = 'maintenance_notice',
  ExpenseNotification = 'expense_notification',
  Custom = 'custom'
}

/**
 * Available delivery methods for communications
 */
export enum DeliveryMethod {
  Email = 'email',
  InApp = 'in_app',
  SMS = 'sms',
  WhatsApp = 'whatsapp'
}

/**
 * Delivery method configuration for a specific communication
 */
export interface DeliveryMethodConfig {
  method: DeliveryMethod;
  enabled: boolean;
  templateContent?: string; // Compact template for non-email methods
  recipientAddress?: string; // Phone number for SMS/WhatsApp, user ID for in-app
}

/**
 * Mail status in the workflow - 4-state system with clear automation
 */
export enum MailStatus {
  PendingReview = 'pending_review', // Waiting for user approval, auto-archived if not approved by send date
  Scheduled = 'scheduled',          // Approved and scheduled for sending, can be paused
  Sent = 'sent',                    // Successfully sent
  Archived = 'archived'             // Auto-archived or manually archived messages
}

/**
 * Priority levels for mail sending
 */
export enum MailPriority {
  Low = 'low',
  Normal = 'normal',
  High = 'high',
  Urgent = 'urgent'
}

/**
 * Automation trigger types
 */
export enum AutomationTrigger {
  Manual = 'manual',
  Scheduled = 'scheduled',
  ContractEvent = 'contract_event',
  PaymentDue = 'payment_due',
  Indexation = 'indexation',
  Maintenance = 'maintenance'
}

/**
 * Mail template definition
 */
export interface MailTemplate {
  templateUID: string;
  name: string;
  documentType: DocumentType;
  subject: string;
  bodyTemplate: string; // HTML template with placeholders
  attachmentTemplate?: string; // PDF template for attachments
  variables: TemplateVariable[];
  isActive: boolean;
  isDefault: boolean;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
}

/**
 * Template variables for dynamic content
 */
export interface TemplateVariable {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'currency' | 'boolean';
  required: boolean;
  defaultValue?: unknown;
  description?: string;
}

/**
 * Mail draft that needs to be reviewed/approved
 */
export interface MailDraft {
  draftUID: string;
  documentType: DocumentType;
  status: MailStatus;
  priority: MailPriority;

  // Recipients
  recipientEmail: string;
  recipientName: string;
  tenantUID?: string;
  contractUID?: string;
  propertyUID?: string;

  // Summary fields for table display (denormalized for quick access)
  propertyName?: string;
  propertyAddress?: string;
  amount?: number; // Payment amount in cents or main currency unit
  
  // Content
  subject: string;
  bodyHtml: string;
  bodyPlain?: string;
  attachments: MailAttachment[];
  
  // Multi-delivery methods
  deliveryMethods: DeliveryMethodConfig[];
  primaryDeliveryMethod: DeliveryMethod; // Default: Email
  
  // Template and variables
  templateUID?: string;
  templateVariables: Record<string, unknown>;
  sendGridTemplateId?: string; // For SendGrid dynamic templates
  
  // Scheduling
  scheduledDate?: Date | Timestamp;
  sendAfter?: Date | Timestamp; // Don't send before this date
  isPaused?: boolean; // Can be paused to prevent sending
  pausedBy?: string;
  pausedAt?: Date | Timestamp;
  pauseReason?: string;
  
  // Automation
  automationTrigger: AutomationTrigger;
  triggerData?: Record<string, unknown>;
  
  // Workflow
  requiresApproval: boolean;
  approvedBy?: string;
  approvedAt?: Date | Timestamp;
  rejectedBy?: string;
  rejectedAt?: Date | Timestamp;
  rejectionReason?: string;
  
  // Delivery
  sentAt?: Date | Timestamp;
  deliveredAt?: Date | Timestamp;
  openedAt?: Date | Timestamp;
  failureReason?: string;
  retryCount: number;
  
  // Audit
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
  updatedBy?: string;
  
  // Notes and comments
  notes?: string;
  internalComments?: MailComment[];
}

/**
 * Mail attachment definition
 */
export interface MailAttachment {
  filename: string;
  contentType: string;
  size: number;
  storageUrl?: string; // Firebase Storage URL
  inline?: boolean;
  cid?: string; // Content-ID for inline images
}

/**
 * Internal comments on mail drafts
 */
export interface MailComment {
  commentUID: string;
  text: string;
  createdBy: string;
  createdAt: Date | Timestamp;
}

/**
 * Mail configuration at different levels
 */
export interface MailConfiguration {
  configUID: string;
  level: 'global' | 'property' | 'contract' | 'tenant';
  entityUID?: string; // propertyUID, contractUID, or tenantUID
  
  // General settings
  isEnabled: boolean;
  requireApproval: boolean;
  defaultTemplateUID?: string;
  
  // Automation settings
  automationSettings: AutomationSettings;
  
  // Delivery settings
  deliverySettings: DeliverySettings;
  
  // Override settings from parent levels
  overrides: ConfigurationOverrides;
  
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
}

/**
 * Automation configuration
 */
export interface AutomationSettings {
  // Invoice automation
  invoiceAutomation: {
    enabled: boolean;
    daysBefore: number; // Days before due date
    includePaymentDetails: boolean;
    includeBankDetails: boolean;
    reminderSettings: ReminderSettings;
  };
  
  // Indexation automation
  indexationAutomation: {
    enabled: boolean;
    noticeMonthsBefore: number;
    includeCalculationDetails: boolean;
  };
  
  // Contract automation
  contractAutomation: {
    renewalNoticeEnabled: boolean;
    renewalMonthsBefore: number;
    terminationNoticeEnabled: boolean;
    terminationDaysBefore: number;
  };
  
  // Maintenance automation
  maintenanceAutomation: {
    notificationEnabled: boolean;
    advanceNoticeDays: number;
  };
}

/**
 * Reminder settings for invoices
 */
export interface ReminderSettings {
  enabled: boolean;
  firstReminderDays: number; // Days after due date
  secondReminderDays: number;
  finalNoticeDays: number;
  escalationEnabled: boolean;
}

/**
 * Delivery and sending settings
 */
export interface DeliverySettings {
  fromEmail: string;
  fromName: string;
  replyToEmail?: string;
  
  // Delivery options
  sendCopyToOwner: boolean;
  sendCopyToManager: boolean;
  trackOpening: boolean;
  trackClicks: boolean;
  
  // Rate limiting
  maxPerHour: number;
  maxPerDay: number;
  
  // Retry settings
  maxRetries: number;
  retryDelayMinutes: number;
}

/**
 * Configuration overrides for inheritance
 */
export interface ConfigurationOverrides {
  disableAutomation?: boolean;
  customTemplates?: Record<DocumentType, string>;
  customFromEmail?: string;
  customFromName?: string;
  customDeliverySettings?: Partial<DeliverySettings>;
}

/**
 * Mail queue entry for processing
 */
export interface MailQueueEntry {
  queueUID: string;
  draftUID: string;
  priority: MailPriority;
  scheduledFor: Date | Timestamp;
  maxRetries: number;
  currentRetries: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  lastAttempt?: Date | Timestamp;
  nextAttempt?: Date | Timestamp;
  errorMessage?: string;
  processingNode?: string; // Which cloud function instance is processing
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

/**
 * Mail delivery tracking
 */
export interface MailDeliveryTracking {
  trackingUID: string;
  draftUID: string;
  recipientEmail: string;
  
  // Delivery events
  events: MailDeliveryEvent[];
  
  // Summary
  delivered: boolean;
  opened: boolean;
  clicked: boolean;
  bounced: boolean;
  complained: boolean;
  
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

/**
 * Individual delivery events
 */
export interface MailDeliveryEvent {
  eventUID: string;
  type: 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'unsubscribed';
  timestamp: Date | Timestamp;
  data?: Record<string, unknown>; // Event-specific data
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Statistics and analytics
 */
export interface MailStatistics {
  period: 'day' | 'week' | 'month' | 'year';
  startDate: Date | Timestamp;
  endDate: Date | Timestamp;
  
  // Counts
  totalSent: number;
  totalDelivered: number;
  totalOpened: number;
  totalClicked: number;
  totalBounced: number;
  totalComplaints: number;
  
  // Rates
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  complaintRate: number;
  
  // By document type
  byDocumentType: Record<DocumentType, MailTypeStatistics>;
  
  // By property/contract
  byProperty: Record<string, MailTypeStatistics>;
  
  createdAt: Date | Timestamp;
}

/**
 * Statistics for a specific type/entity
 */
export interface MailTypeStatistics {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complaints: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
}

/**
 * Bulk mail operation for sending multiple mails at once
 */
export interface BulkMailOperation {
  operationUID: string;
  name: string;
  documentType: DocumentType;
  templateUID: string;
  
  // Targeting
  recipientCriteria: RecipientCriteria;
  estimatedRecipients: number;
  actualRecipients: number;
  
  // Generated drafts
  draftUIDs: string[];
  
  // Status
  status: 'preparing' | 'ready' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  
  // Results
  successCount: number;
  failureCount: number;
  pendingCount: number;
  
  // Scheduling
  scheduledFor?: Date | Timestamp;
  
  // Audit
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
  completedAt?: Date | Timestamp;
}

/**
 * Criteria for selecting recipients in bulk operations
 */
export interface RecipientCriteria {
  // Property filters
  propertyUIDs?: string[];
  propertyTypes?: string[];
  
  // Contract filters
  contractStatuses?: string[];
  contractTypes?: string[];
  rentRange?: { min: number; max: number };
  
  // Tenant filters
  tenantTypes?: string[];
  
  // Date filters
  contractStartAfter?: Date | Timestamp;
  contractStartBefore?: Date | Timestamp;
  
  // Custom filters
  customFilters?: Record<string, unknown>;
}

/**
 * Enhanced mail draft with multi-delivery support
 */ 